export const MEDIAPIPE_MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';
export const MEDIAPIPE_WASM_ROOT = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm';

const KEYPOINT_INDEXES = {
  left: { shoulder: 11, elbow: 13, wrist: 15, hip: 23, knee: 25, ankle: 27 },
  right: { shoulder: 12, elbow: 14, wrist: 16, hip: 24, knee: 26, ankle: 28 },
};

function cancellationError() {
  const error = new Error('Pose provider setup was cancelled');
  error.name = 'AbortError';
  return error;
}

function normalizeLandmark(point) {
  if (!point) return null;
  return {
    x: point.x,
    y: point.y,
    z: point.z,
    confidence: point.visibility ?? point.presence ?? 0,
  };
}

export function normalizeMediaPipeResult(result, timestamp) {
  const landmarks = result?.landmarks?.[0];
  if (!landmarks) return null;
  const sides = {};
  Object.entries(KEYPOINT_INDEXES).forEach(([side, indexes]) => {
    sides[side] = {};
    Object.entries(indexes).forEach(([name, index]) => {
      sides[side][name] = normalizeLandmark(landmarks[index]);
    });
  });
  return { timestamp, sides };
}

// This pinned browser ESM has a native dynamic WASM-loader import. Let the
// browser resolve it rather than asking webpack to build an expression context.
// Model and WASM assets already use the same external, version-pinned boundary.
const loadMediaPipeVision = () => import(/* webpackIgnore: true */ 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/vision_bundle.mjs');

export async function createDefaultMediaPipeLandmarker(loadVision = loadMediaPipeVision) {
  const { FilesetResolver, PoseLandmarker } = await loadVision();
  const fileset = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_ROOT);
  const options = delegate => ({
    baseOptions: { modelAssetPath: MEDIAPIPE_MODEL_URL, delegate },
    runningMode: 'VIDEO',
    numPoses: 1,
    minPoseDetectionConfidence: 0.5,
    minPosePresenceConfidence: 0.5,
    minTrackingConfidence: 0.5,
    outputSegmentationMasks: false,
  });
  try {
    return {
      landmarker: await PoseLandmarker.createFromOptions(fileset, options('GPU')),
      requestedDelegate: 'GPU',
      verifiedBackend: null,
      fallbackReason: null,
    };
  } catch (gpuError) {
    return {
      landmarker: await PoseLandmarker.createFromOptions(fileset, options('CPU')),
      requestedDelegate: 'CPU',
      verifiedBackend: null,
      fallbackReason: gpuError?.message || 'GPU delegate unavailable',
    };
  }
}

export class MediaPipePoseProvider {
  constructor({
    mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : null,
    createLandmarker = createDefaultMediaPipeLandmarker,
    schedule = (callback, delay) => setTimeout(callback, delay),
    cancelSchedule = timer => clearTimeout(timer),
    now = () => performance.now(),
    documentRef = typeof document !== 'undefined' ? document : { hidden: false },
    cadenceMs = 100,
  } = {}) {
    this.mediaDevices = mediaDevices;
    this.createLandmarker = createLandmarker;
    this.schedule = schedule;
    this.cancelSchedule = cancelSchedule;
    this.now = now;
    this.documentRef = documentRef;
    this.cadenceMs = cadenceMs;
    this.running = false;
    this.inferenceEnabled = true;
    this.inFlight = false;
    this.lifecycleGeneration = 0;
    this.timer = null;
    this.stream = null;
    this.video = null;
    this.landmarker = null;
    this.onObservation = null;
    this.onError = null;
    this.diagnostic = { provider: 'MediaPipe Pose Landmarker Lite', requestedDelegate: null, verifiedBackend: null, fallbackReason: null };
  }

  async start({ video, onObservation, onError }) {
    if (!this.mediaDevices?.getUserMedia) throw new Error('Camera access is not supported on this device');
    if (!video) throw new Error('A video preview is required');
    await this.stop();
    const generation = ++this.lifecycleGeneration;
    this.video = video;
    this.onObservation = onObservation;
    this.onError = onError;
    try {
      const stream = await this.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'user' },
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 24, max: 30 },
        },
      });
      if (generation !== this.lifecycleGeneration) {
        stream.getTracks().forEach(track => track.stop());
        throw cancellationError();
      }
      this.stream = stream;
      this.video.srcObject = stream;
      await this.video.play?.();
      if (generation !== this.lifecycleGeneration) throw cancellationError();
      const created = await this.createLandmarker();
      const landmarker = created.landmarker || created;
      if (generation !== this.lifecycleGeneration) {
        await Promise.resolve(landmarker.close?.());
        throw cancellationError();
      }
      this.landmarker = landmarker;
      this.diagnostic = {
        provider: 'MediaPipe Pose Landmarker Lite',
        requestedDelegate: created.requestedDelegate || null,
        verifiedBackend: created.verifiedBackend || null,
        fallbackReason: created.fallbackReason || null,
      };
      this.running = true;
      this.inferenceEnabled = true;
      this.scheduleNext();
      return this.diagnostic;
    } catch (error) {
      if (generation === this.lifecycleGeneration) await this.stop();
      throw error;
    }
  }

  scheduleNext() {
    if (!this.running) return;
    const generation = this.lifecycleGeneration;
    this.timer = this.schedule(async () => {
      try {
        await this.tick();
      } catch (error) {
        if (generation !== this.lifecycleGeneration) return;
        const onError = this.onError;
        try {
          await this.stop();
        } catch {
          // Cleanup is best-effort; the inference failure remains authoritative.
        } finally {
          try {
            await Promise.resolve(onError?.(error));
          } catch {
            // Error reporting must not create a second unhandled rejection.
          }
        }
        return;
      }
      if (generation === this.lifecycleGeneration && this.running) this.scheduleNext();
    }, this.cadenceMs);
  }

  setInferenceEnabled(enabled) {
    this.inferenceEnabled = Boolean(enabled);
  }

  async tick() {
    if (!this.running || !this.inferenceEnabled || this.inFlight || this.documentRef?.hidden || !this.landmarker || !this.video) return null;
    this.inFlight = true;
    const generation = this.lifecycleGeneration;
    const onObservation = this.onObservation;
    const timestamp = this.now();
    try {
      const result = await Promise.resolve(this.landmarker.detectForVideo(this.video, timestamp));
      if (generation !== this.lifecycleGeneration || !this.running) return null;
      const observation = normalizeMediaPipeResult(result, timestamp);
      if (observation) await Promise.resolve(onObservation?.(observation));
      return observation;
    } finally {
      this.inFlight = false;
    }
  }

  async stop() {
    this.lifecycleGeneration += 1;
    this.running = false;
    if (this.timer != null) {
      try { this.cancelSchedule(this.timer); } catch { /* Best-effort timer cleanup. */ }
    }
    this.timer = null;
    const stream = this.stream;
    const landmarker = this.landmarker;
    const video = this.video;
    this.stream = null;
    this.landmarker = null;
    this.video = null;
    this.inFlight = false;
    this.onObservation = null;
    this.onError = null;
    if (stream) {
      try {
        stream.getTracks().forEach(track => {
          try { track.stop(); } catch { /* Best-effort track cleanup. */ }
        });
      } catch { /* Best-effort stream cleanup. */ }
    }
    try { await Promise.resolve(landmarker?.close?.()); } catch { /* Best-effort model cleanup. */ }
    try { if (video) video.srcObject = null; } catch { /* Best-effort preview cleanup. */ }
  }
}
